import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Warning, Lightning, DownloadSimple } from '@phosphor-icons/react';
import { jsPDF } from 'jspdf';

export default function InvoicePage() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { getOrgHeaders, API } = useAuth();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/payments/invoice/${transactionId}`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setInv(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Failed to load invoice'))
      .finally(() => setLoading(false));
  }, [API, transactionId, getOrgHeaders]);

  const handlePrint = () => window.print();

  // Branded, text-based PDF (crisp; works for both INR and USD invoices).
  // jsPDF's core fonts can't render the ₹ glyph, so INR uses an "Rs." prefix.
  const handleDownloadPdf = () => {
    const invc = inv;
    const loc = invc.currency === 'USD' ? 'en-US' : 'en-IN';
    const pdfCur = invc.currency === 'USD' ? '$' : 'Rs. ';
    const money = (n) => `${pdfCur}${Number(n || 0).toLocaleString(loc, { minimumFractionDigits: 2 })}`;
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const M = 15;
    let y = 20;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(79, 70, 229);
    doc.text('Inspiro Projects', M, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(150);
    doc.text('by INPW Technologies Pvt Ltd', M, y + 5);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(24, 24, 27);
    doc.text(invc.invoice_title || 'TAX INVOICE', pageW - M, y, { align: 'right' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(90);
    doc.text(`Invoice #: ${invc.invoice_number}`, pageW - M, y + 6, { align: 'right' });
    doc.text(`Date: ${new Date(invc.invoice_date).toLocaleDateString(loc, { day: '2-digit', month: 'short', year: 'numeric' })}`, pageW - M, y + 11, { align: 'right' });
    doc.text(`Status: ${String(invc.payment.status).toUpperCase()}`, pageW - M, y + 16, { align: 'right' });
    y += 26; doc.setDrawColor(220); doc.line(M, y, pageW - M, y); y += 8;
    // From / Bill To
    doc.setFontSize(8); doc.setTextColor(150); doc.text('FROM', M, y); doc.text('BILL TO', pageW / 2, y);
    y += 5; doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5); doc.setTextColor(24, 24, 27);
    doc.text(invc.seller.name, M, y); doc.text(invc.buyer.name || '-', pageW / 2, y);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(90);
    const sAddr = doc.splitTextToSize(invc.seller.address || '', pageW / 2 - M - 5);
    doc.text(sAddr, M, y + 5); doc.text(invc.seller.email || '', M, y + 5 + sAddr.length * 4);
    const bLines = [invc.buyer.address_line1, [invc.buyer.city, invc.buyer.state, invc.buyer.pincode].filter(Boolean).join(', '), invc.buyer.country, invc.buyer.gst_number ? `GSTIN: ${invc.buyer.gst_number}` : ''].filter(Boolean);
    doc.text(bLines.length ? bLines : ['-'], pageW / 2, y + 5);
    y += 5 + Math.max(sAddr.length * 4 + 5, bLines.length * 4) + 8;
    // Items header
    doc.setFillColor(244, 244, 245); doc.rect(M, y - 4, pageW - 2 * M, 8, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(90);
    doc.text('DESCRIPTION', M + 2, y + 1);
    doc.text('QTY', pageW - M - 55, y + 1, { align: 'right' });
    doc.text('RATE', pageW - M - 30, y + 1, { align: 'right' });
    doc.text('AMOUNT', pageW - M - 2, y + 1, { align: 'right' });
    y += 9; doc.setFont('helvetica', 'normal'); doc.setTextColor(24, 24, 27); doc.setFontSize(9);
    invc.items.forEach((it) => {
      const desc = doc.splitTextToSize(it.description, pageW - 2 * M - 72);
      doc.text(desc, M + 2, y);
      doc.text(String(it.quantity), pageW - M - 55, y, { align: 'right' });
      doc.text(money(it.unit_price), pageW - M - 30, y, { align: 'right' });
      doc.text(money(it.amount), pageW - M - 2, y, { align: 'right' });
      y += Math.max(desc.length * 4.5, 6) + 2;
    });
    doc.setDrawColor(220); doc.line(M, y, pageW - M, y); y += 6;
    const tx = pageW - M - 62;
    const row = (label, val, bold) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(bold ? 11 : 9.5);
      doc.setTextColor(bold ? 24 : 90, bold ? 24 : 90, bold ? 27 : 90);
      doc.text(label, tx, y); doc.text(val, pageW - M - 2, y, { align: 'right' }); y += bold ? 8 : 5.5;
    };
    if (invc.gst_applies) {
      row('Taxable Amount', money(invc.taxable_amount));
      if (invc.is_intra_state) { row('CGST (9%)', money(invc.cgst)); row('SGST (9%)', money(invc.sgst)); }
      else row('IGST (18%)', money(invc.igst));
    } else {
      row('Subtotal', money(invc.subtotal));
    }
    doc.setDrawColor(24, 24, 27); doc.line(tx, y - 2, pageW - M, y - 2); y += 2;
    row('Total', money(invc.total), true);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(150);
    doc.text(invc.gst_applies ? `Place of supply: ${invc.buyer.state || invc.seller.state}` : 'International invoice - no GST applicable.', M, 285);
    doc.text('Thank you for your business.', M, 290);
    doc.save(`${invc.invoice_number || 'invoice'}.pdf`);
  };

  if (loading) return <Centered><p className="text-zinc-400 text-sm">Loading invoice…</p></Centered>;
  if (error || !inv) return (
    <Centered>
      <Warning weight="fill" size={40} className="text-red-500 mx-auto mb-3" />
      <p className="text-red-600 text-sm mb-4 text-center">{error || 'Invoice not available'}</p>
      <Button onClick={() => navigate('/billing')} variant="outline"><ArrowLeft size={14} className="mr-2" /> Back to Billing</Button>
    </Centered>
  );

  const invoiceDate = new Date(inv.invoice_date);
  const cur = inv.currency_symbol || '₹';
  const locale = inv.currency === 'USD' ? 'en-US' : 'en-IN';
  const cycleLabel = inv.items[0]?.billing_cycle === 'yearly' ? 'Year' : 'Month';

  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4 print:bg-white print:py-0">
      <style>{`@media print { .no-print { display: none !important; } body { background: white !important; } }`}</style>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6 no-print">
          <Button variant="ghost" onClick={() => navigate('/billing')} data-testid="back-to-billing-btn">
            <ArrowLeft size={14} className="mr-2" /> Back to Billing
          </Button>
          <div className="flex items-center gap-2">
            <Button onClick={handleDownloadPdf} variant="outline" data-testid="download-invoice-pdf-btn">
              <DownloadSimple size={14} className="mr-2" /> Download PDF
            </Button>
            <Button onClick={handlePrint} className="bg-indigo-600 hover:bg-indigo-700" data-testid="print-invoice-btn">
              <Printer size={14} className="mr-2" /> Print / Save as PDF
            </Button>
          </div>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl shadow-sm p-10 print:shadow-none print:border-none print:p-4" data-testid="invoice-document">
          {/* Header */}
          <div className="flex justify-between items-start mb-8 pb-6 border-b border-zinc-200">
            <div>
              <h1 className="font-['Outfit'] text-3xl font-bold text-zinc-900 tracking-tight" data-testid="invoice-title">{inv.invoice_title || 'TAX INVOICE'}</h1>
              <p className="text-xs text-zinc-500 mt-2">Invoice #: <span className="font-semibold text-zinc-800" data-testid="invoice-number">{inv.invoice_number}</span></p>
              <p className="text-xs text-zinc-500">Date: <span className="font-medium text-zinc-800">{invoiceDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></p>
            </div>
            <div className="text-right flex items-start gap-3">
              <div>
                <h2 className="text-xl font-bold text-indigo-600">Inspiro Projects</h2>
                <p className="text-[10px] text-zinc-400">by INPW Technologies Pvt Ltd</p>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-[10px] font-semibold ${
                  inv.payment.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`} data-testid="invoice-status">
                  {inv.payment.status.toUpperCase()}
                </span>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                <Lightning weight="fill" className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          {/* Seller & Buyer */}
          <div className="grid grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-widest mb-2">From</h3>
              <p className="text-sm font-semibold text-zinc-900">{inv.seller.name}</p>
              <p className="text-xs text-zinc-600">{inv.seller.address}</p>
              <p className="text-xs text-zinc-600">{inv.seller.email}</p>
            </div>
            <div>
              <h3 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-widest mb-2">Bill To</h3>
              <p className="text-sm font-semibold text-zinc-900" data-testid="buyer-name">{inv.buyer.name || '—'}</p>
              {inv.buyer.address_line1 && <p className="text-xs text-zinc-600">{inv.buyer.address_line1}</p>}
              {(inv.buyer.city || inv.buyer.state || inv.buyer.pincode) && (
                <p className="text-xs text-zinc-600">{[inv.buyer.city, inv.buyer.state, inv.buyer.pincode].filter(Boolean).join(', ')}</p>
              )}
              {inv.buyer.gst_number && <p className="text-xs text-zinc-600" data-testid="buyer-gst">GSTIN: {inv.buyer.gst_number}</p>}
              {!inv.buyer.address_line1 && (
                <p className="text-[10px] text-amber-600 italic mt-1 no-print">
                  Tip: Admins can add GSTIN &amp; billing address in Settings → Billing Details.
                </p>
              )}
            </div>
          </div>

          {/* Line items */}
          <table className="w-full text-sm mb-6" data-testid="invoice-items-table">
            <thead>
              <tr className="bg-zinc-50 border-y border-zinc-200">
                <th className="text-left py-3 px-3 font-semibold text-zinc-600 text-xs">#</th>
                <th className="text-left py-3 px-3 font-semibold text-zinc-600 text-xs">Description</th>
                <th className="text-center py-3 px-3 font-semibold text-zinc-600 text-xs">SAC</th>
                <th className="text-center py-3 px-3 font-semibold text-zinc-600 text-xs">Qty</th>
                <th className="text-right py-3 px-3 font-semibold text-zinc-600 text-xs">Rate</th>
                <th className="text-right py-3 px-3 font-semibold text-zinc-600 text-xs">Amount</th>
              </tr>
            </thead>
            <tbody>
              {inv.items.map((item, idx) => (
                <tr key={idx} className="border-b border-zinc-100">
                  <td className="py-3 px-3 text-zinc-600 text-xs">{idx + 1}</td>
                  <td className="py-3 px-3 text-zinc-900 font-medium">
                    {item.description}
                    <span className="block text-[11px] text-zinc-500 mt-0.5">
                      {item.quantity} seat(s) @ {cur}{item.unit_price}/{cycleLabel.toLowerCase()}
                    </span>
                    {inv.gst_applies && <span className="block text-[10px] text-zinc-400 mt-0.5 italic">Price inclusive of 18% GST</span>}
                  </td>
                  <td className="py-3 px-3 text-center text-zinc-600 text-xs">{item.sac_code || '—'}</td>
                  <td className="py-3 px-3 text-center text-zinc-600 text-xs">{item.quantity}</td>
                  <td className="py-3 px-3 text-right text-zinc-600 text-xs">{cur}{item.unit_price.toLocaleString(locale)}</td>
                  <td className="py-3 px-3 text-right font-medium text-zinc-900">{cur}{item.amount.toLocaleString(locale, { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-72 space-y-1.5 text-sm">
              {inv.gst_applies ? (
                <>
                  <Row label="Taxable Amount" value={`${cur}${inv.taxable_amount.toLocaleString(locale, { minimumFractionDigits: 2 })}`} />
                  {inv.is_intra_state ? (
                    <>
                      <Row label="CGST (9%)" value={`${cur}${inv.cgst.toLocaleString(locale, { minimumFractionDigits: 2 })}`} />
                      <Row label="SGST (9%)" value={`${cur}${inv.sgst.toLocaleString(locale, { minimumFractionDigits: 2 })}`} />
                    </>
                  ) : (
                    <Row label="IGST (18%)" value={`${cur}${inv.igst.toLocaleString(locale, { minimumFractionDigits: 2 })}`} />
                  )}
                </>
              ) : (
                <Row label="Subtotal" value={`${cur}${inv.subtotal.toLocaleString(locale, { minimumFractionDigits: 2 })}`} />
              )}
              <div className="flex justify-between py-2 border-t-2 border-zinc-900 font-bold text-base">
                <span>Total</span>
                <span data-testid="invoice-total">{cur}{inv.total.toLocaleString(locale, { minimumFractionDigits: 2 })}</span>
              </div>
              {inv.gst_applies
                ? <p className="text-[10px] text-zinc-400 pt-1">Place of supply: {inv.buyer.state || inv.seller.state} ({inv.is_intra_state ? 'Intra-state' : 'Inter-state'})</p>
                : <p className="text-[10px] text-zinc-400 pt-1" data-testid="invoice-no-gst-note">International invoice — no GST applicable.</p>}
            </div>
          </div>

          {/* Payment info */}
          <div className="bg-zinc-50 rounded-lg p-4 mb-6 text-xs">
            <h4 className="font-semibold text-zinc-700 mb-2 text-[11px] uppercase tracking-wider">Payment Details</h4>
            <div className="grid grid-cols-2 gap-2 text-zinc-600">
              <div>Payment Method: <span className="font-medium text-zinc-800">Razorpay</span></div>
              <div>Transaction ID: <span className="font-medium text-zinc-800 font-mono text-[10px]">{inv.payment.payment_id || inv.transaction_id}</span></div>
              <div>Currency: <span className="font-medium text-zinc-800">{inv.payment.currency}</span></div>
              <div>Status: <span className="font-medium text-emerald-700">Paid</span></div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-zinc-200 pt-4 text-[11px] text-zinc-400 text-center space-y-1">
            <p>This is a computer-generated invoice and does not require a physical signature.</p>
            <p>For any queries, contact accounts@inspiroworks.com</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-zinc-500 text-xs">{label}</span>
      <span className="text-zinc-700 text-xs">{value}</span>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-zinc-50">{children}</div>;
}
